import * as React from "react";

import CodeCopyBtn from '@view/components/codeCopyBtn/CodeCopyBtn';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './ChatMessage.module.scss';
import ChatMessageOptionsMenu from './chatMessageOptionsMenu/ChatMessageOptionsMenu';
import SyntaxHighlighterCode from "./syntaxHighlighterCode/SyntaxHighlighterCode";
import { ConversationMessageInterface, ToolCallInterface, VisionPromptUtil } from "@dashart/dashart-gpt-shared-library";
import { ToolProgressInterface } from "../../../app/services/CustomEndpointChatService";
import { ChatCompletionStatsInterface } from "../../../app/interfaces/ChatCompletionResultInterface";
import { ImagePreview } from "./imagePreview/ImagePreview";
import ToolProgressList from "../toolProgressList/ToolProgressList";

interface ChatMessageProps {
    message: ConversationMessageInterface;
    index: number;
    onForkRequestHandler?: (messageIndex: number) => void;
}

const roleIcons = {
    bot: '🤖',
    user: '👤',
    default: '🖥️'
} as const;

const ButtonSection = memo(({ children }: { children: React.ReactNode }) => (
    <pre>
        <div className="print-hidden">
            <CodeCopyBtn>{children}</CodeCopyBtn>
        </div>
        {children}
    </pre>
));

/**
 * Tries to pretty-print a tool_call arguments string as JSON; falls back to
 * the raw string if parsing fails (the stream may render partial fragments
 * mid-call).
 */
const formatToolCallArguments = (args: string): string => {
    if (!args) return '';
    try {
        return JSON.stringify(JSON.parse(args), null, 2);
    } catch {
        return args;
    }
};

interface ToolCallsBlockProps {
    toolCalls: ToolCallInterface[];
}

interface MessageStatsProps {
    stats: ChatCompletionStatsInterface;
}

interface ReasoningBlockProps {
    reasoning: string;
}

const ReasoningBlock = memo(({ reasoning }: ReasoningBlockProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <details
            className={styles.reasoningBlock}
            open={open}
            onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
        >
            <summary className={styles.reasoningSummary}>
                <span className={styles.reasoningSummaryLabel}>
                    {t('components.chat_message.reasoning.label')}
                </span>
                <span className={styles.reasoningSummaryToggle}>
                    {open
                        ? t('components.chat_message.reasoning.hide')
                        : t('components.chat_message.reasoning.show')}
                </span>
            </summary>
            <div className={styles.reasoningColumn}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                >
                    {reasoning}
                </ReactMarkdown>
            </div>
        </details>
    );
});

const MessageStats = memo(({ stats }: MessageStatsProps) => {
    const { t } = useTranslation();

    return (
        <div className={styles.messageStats}>
            <span className={styles.messageStatsItem}>
                {t('components.chat_message.stats.output')}: {stats.completionTokens.toLocaleString()}
            </span>
            <span className={styles.messageStatsItem}>
                {t('components.chat_message.stats.input')}: {stats.promptTokens.toLocaleString()}
            </span>
            <span className={styles.messageStatsItem}>
                {t('components.chat_message.stats.total')}: {stats.totalTokens.toLocaleString()}
            </span>
            <span className={styles.messageStatsItem}>
                {t('components.chat_message.stats.tokens_per_second')}: {stats.tokensPerSecond.toFixed(1).replace(/\.0$/, '')}
            </span>
        </div>
    );
});

const ToolCallsBlock = memo(({ toolCalls }: ToolCallsBlockProps) => {
    const { t } = useTranslation();

    return (
        <div className={styles.toolCallsBlock}>
            <div className={styles.toolCallsHeader}>
                {t('chat_screen.chat_message.tool_calls.heading', { count: toolCalls.length })}
            </div>
            {toolCalls.map((toolCall, index) => (
                <details
                    key={toolCall.id || `tool-call-${index}`}
                    className={styles.toolCall}
                    open={true}
                >
                    <summary className={styles.toolCallSummary}>
                        <span className={styles.toolCallName}>
                            {toolCall.function?.name || t('chat_screen.chat_message.tool_calls.unknown_tool')}
                        </span>
                        {toolCall.id && (
                            <span className={styles.toolCallId}>{toolCall.id}</span>
                        )}
                    </summary>
                    <div className={styles.toolCallBody}>
                        <div className={styles.toolCallSectionLabel}>
                            {t('chat_screen.chat_message.tool_calls.arguments')}
                        </div>
                        <pre className={styles.toolCallArguments}>
                            <code>{formatToolCallArguments(toolCall.function?.arguments ?? '')}</code>
                        </pre>
                    </div>
                </details>
            ))}
        </div>
    );
});

const ChatMessage = memo(({ message, index, onForkRequestHandler }: ChatMessageProps) => {
    const { t } = useTranslation();

    const isVisionRequest = VisionPromptUtil.isVisionRequest(message.message.content);
    const visionContent = isVisionRequest ? VisionPromptUtil.convertVisionPromptToContent(message.message.content) : null;
    const content = visionContent ? visionContent.prompt : message.message.content;
    const toolCalls = (message.message as any).tool_calls as ToolCallInterface[] | undefined;
    const toolCallId = (message.message as any).tool_call_id as string | undefined;
    const toolProgress = (message.message as any).tool_progress as ToolProgressInterface[] | undefined;
    const stats = (message.message as any).stats as ChatCompletionStatsInterface | undefined;
    const isToolResult = message.message.role === 'tool';

    /**
     * Handle the fork request
     */
    const handleForkContent = useCallback(() => {
        if (onForkRequestHandler) {
            onForkRequestHandler(index);
        }
    }, [onForkRequestHandler, index]);

    return (
        <div
            className={`${styles.ChatMessage} markdown-body col-12 flex flex-row pr-5 ${index % 2 ? 'bg-white-alpha-90' : 'bg-white-alpha-50'
                }`}
        >
            <div className="absolute right-0 top-0 p-2 pr-3 print-hidden">
                <ChatMessageOptionsMenu
                    messageContents={message.message.content}
                    messageIndex={index}
                    onForkRequestHandler={handleForkContent}
                />
            </div>

            <div className={styles.iconColumn}>
                {roleIcons[message.message.role as keyof typeof roleIcons] || roleIcons.default}
            </div>

            <div className={styles.messageColumn}>
                {(visionContent && visionContent.images.length > 0) && (
                    <div className={styles.imageColumn}>
                        {visionContent.images.map((image) => (
                            <ImagePreview
                                src={image?.imageUrl}
                            />
                        ))}
                    </div>
                )}

                {message.message.reasoning && (
                    <ReasoningBlock reasoning={message.message.reasoning} />
                )}

                {isToolResult && toolCallId && (
                    <div className={styles.toolResultHeader}>
                        {t('chat_screen.chat_message.tool_calls.tool_result', { id: toolCallId })}
                    </div>
                )}

                {toolProgress?.length > 0 && (
                    <ToolProgressList progress={toolProgress} />
                )}

                {content && (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            pre: ButtonSection,
                            code: SyntaxHighlighterCode
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                )}

                {toolCalls?.length > 0 && (
                    <ToolCallsBlock toolCalls={toolCalls} />
                )}

                {stats && (
                    <MessageStats stats={stats} />
                )}
            </div>
        </div>
    );
});

export default ChatMessage;